import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { customerApi, userApi } from '../../api/endpoints.js';
import { useApi } from '../../hooks/useApi.js';
import { useToast } from '../../hooks/useToast.jsx';
import { useApiError } from '../../hooks/useApiError.js';
import PageHeader from '../../components/PageHeader.jsx';
import DataTable from '../../components/DataTable.jsx';
import Toolbar from '../../components/Toolbar.jsx';
import StatCard from '../../components/StatCard.jsx';
import Badge from '../../components/Badge.jsx';
import { Select, TextInput } from '../../components/Field.jsx';
import { formatCurrency, formatDate, fullName } from '../../utils/format.js';
import { CUSTOMER_STATUSES } from '../../constants.js';

/**
 * THE ADMINISTRATOR'S CUSTOMER TABLE - one row per account, everything on it.
 *
 * This is a place to LOOK, not a place to prospect. An administrator does not
 * call anybody and does not fill in a qualification form, so none of that is
 * offered here. What they need is the whole book in one table: where each
 * account stands, whether the deal was signed, what the qualification scored,
 * who is on it, and what has been invoiced and collected.
 *
 * The one thing you can change from this screen is the sales owner, because
 * "this account has no rep" and "give it to her" are the same thought - having
 * to open the record to act on it is what makes that reassignment not happen.
 *
 * Anything deeper - the questionnaire answers, the written brief, the thread -
 * is one click away on the record itself.
 */
const CustomerOverviewPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const toMessage = useApiError();
  const locale = i18n.resolvedLanguage;

  const [query, setQuery] = useState({ q: '', status: '', signed: '', ownerCommercialId: '' });

  const { data, loading, refresh } = useApi(
    () =>
      customerApi.list({
        q: query.q || undefined,
        status: query.status || undefined,
        signed: query.signed || undefined,
        ownerCommercialId: query.ownerCommercialId || undefined,
        totals: 'yes',
        pageSize: 200,
      }),
    [query],
  );
  const { data: directory } = useApi(() => userApi.directory(), []);

  const rows = data?.items ?? [];
  const commercials = (directory?.items ?? []).filter((person) => person.role === 'COMMERCIAL');

  const patch = (part) => setQuery((current) => ({ ...current, ...part }));

  const assign = async (customer, commercialId) => {
    try {
      await customerApi.assign(customer.id, { ownerCommercialId: commercialId || null });
      toast.success(t('customers.assign'));
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  // Computed over what is on screen, so the totals always match the filters.
  const sum = (pick) => rows.reduce((total, row) => total + (pick(row) || 0), 0);
  const totals = {
    customers: rows.length,
    signed: rows.filter((row) => row.signed).length,
    pipeline: sum((row) => row.estimatedValue),
    invoiced: sum((row) => row.billing?.invoiced),
    paid: sum((row) => row.billing?.paid),
    balance: sum((row) => row.billing?.balance),
  };

  const money = (value) => formatCurrency(value, locale);

  const columns = [
    {
      key: 'companyName',
      header: t('customers.companyName'),
      render: (row) => (
        <div className="cell-stack">
          <span className="cell-strong">{row.companyName}</span>
          <span className="cell-muted">{[row.city, row.country].filter(Boolean).join(', ') || '-'}</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (row) => <Badge value={row.status} label={t(`customers.status.${row.status}`)} />,
    },
    {
      key: 'signed',
      header: t('customers.signed'),
      render: (row) => (
        <div className="cell-stack">
          <Badge
            value={row.signed ? 'SIGNED' : 'NOT_SIGNED'}
            label={row.signed ? t('common.yes') : t('common.no')}
          />
          {row.signed && row.signedAt ? (
            <span className="cell-muted">{formatDate(row.signedAt, locale)}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'lead',
      header: t('customers.qualification'),
      // The score, and whether there is an answered questionnaire at all - the
      // answers themselves are on the record.
      render: (row) =>
        row.lead ? (
          <Badge value={row.lead.temperature} label={`${row.lead.score}/100`} />
        ) : (
          <span className="cell-muted">{t('customers.notQualified')}</span>
        ),
    },
    { key: 'telepro', header: t('customers.telepro'), render: (row) => fullName(row.telepro) },
    {
      key: 'commercial',
      header: t('customers.commercial'),
      // Editable in place: reassigning is the one action this screen is for.
      render: (row) => (
        <div onClick={(event) => event.stopPropagation()}>
          <Select
            value={row.ownerCommercialId ?? ''}
            onChange={(value) => assign(row, value)}
            placeholder={t('common.unassigned')}
            options={commercials.map((person) => ({ value: person.id, label: fullName(person) }))}
            aria-label={t('customers.commercial')}
          />
        </div>
      ),
    },
    {
      key: 'estimatedValue',
      header: t('customers.estimatedValue'),
      className: 'cell-right',
      render: (row) => money(row.estimatedValue),
    },
    {
      key: 'invoiced',
      header: t('receivables.invoiced'),
      className: 'cell-right',
      render: (row) => (row.billing?.invoices ? money(row.billing.invoiced) : '-'),
    },
    {
      key: 'balance',
      header: t('receivables.balance'),
      className: 'cell-right',
      render: (row) => {
        if (!row.billing?.invoices) return '-';
        if (row.billing.overdue > 0) {
          return <Badge value="LOST" label={money(row.billing.balance)} />;
        }
        return row.billing.balance > 0 ? money(row.billing.balance) : t('receivables.settled');
      },
    },
  ];

  return (
    <>
      <PageHeader title={t('admin.customers')} subtitle={t('admin.customersHint')} />

      <div className="stat-grid">
        <StatCard label={t('dashboard.customers')} value={totals.customers} />
        <StatCard label={t('customers.signed')} value={totals.signed} />
        <StatCard label={t('dashboard.pipelineValue')} value={money(totals.pipeline)} />
        <StatCard label={t('receivables.invoiced')} value={money(totals.invoiced)} />
        <StatCard label={t('receivables.collected')} value={money(totals.paid)} tone="success" />
        <StatCard
          label={t('receivables.balance')}
          value={money(totals.balance)}
          tone={totals.balance > 0 ? 'danger' : 'default'}
        />
      </div>

      <Toolbar>
        <TextInput
          value={query.q}
          onChange={(value) => patch({ q: value })}
          placeholder={t('common.search')}
          aria-label={t('common.search')}
        />
        <Select
          value={query.status}
          onChange={(value) => patch({ status: value })}
          placeholder={t('common.all')}
          options={CUSTOMER_STATUSES.map((status) => ({
            value: status,
            label: t(`customers.status.${status}`),
          }))}
        />
        <Select
          value={query.signed}
          onChange={(value) => patch({ signed: value })}
          placeholder={t('customers.anySignature')}
          options={[
            { value: 'yes', label: t('customers.signedOnly') },
            { value: 'no', label: t('customers.notSigned') },
          ]}
        />
        <Select
          value={query.ownerCommercialId}
          onChange={(value) => patch({ ownerCommercialId: value })}
          placeholder={t('admin.anyCommercial')}
          options={commercials.map((person) => ({ value: person.id, label: fullName(person) }))}
        />
        <span className="toolbar__count">
          {rows.length} {t('common.results')}
        </span>
      </Toolbar>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        emptyTitle={t('customers.emptyOwn')}
        onRowClick={(row) => navigate(`/customers/${row.id}`)}
      />
    </>
  );
};

export default CustomerOverviewPage;
